/** Prefijos internacionales para teléfono (orden: más largo primero para parseo). */
export const PHONE_PREFIX_OPTIONS = [
    {value: '+521', label: 'México móvil (+521)'},
    {value: '+52', label: 'México (+52)'},
    {value: '+1', label: 'EE. UU. / Canadá (+1)'},
    {value: '+34', label: 'España (+34)'},
    {value: '+54', label: 'Argentina (+54)'},
    {value: '+57', label: 'Colombia (+57)'},
    {value: '+51', label: 'Perú (+51)'},
    {value: '+56', label: 'Chile (+56)'},
    {value: '+593', label: 'Ecuador (+593)'},
    {value: '+502', label: 'Guatemala (+502)'}
];

export const DEFAULT_PHONE_PREFIX = '+52';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidEmail = email => EMAIL_REGEX.test(String(email || '').trim());

export const sanitizeIntegerInput = value => {
    const raw = String(value ?? '');
    return raw.replace(/\D+/g, '');
};

export const sanitizeDecimalInput = (value, decimals = 2) => {
    const raw = String(value ?? '').replace(/,/g, '.');
    const cleaned = raw.replace(/[^\d.]/g, '');

    if (!cleaned) return '';

    const firstDot = cleaned.indexOf('.');
    if (firstDot === -1) {
        return cleaned;
    }

    const intPart = cleaned.slice(0, firstDot);
    const decimalPart = cleaned
        .slice(firstDot + 1)
        .replace(/\./g, '')
        .slice(0, decimals);

    return `${intPart}.${decimalPart}`;
};

export const sanitizePhoneInput = value => {
    const digits = sanitizeIntegerInput(value);
    return digits.slice(0, 15);
};

/** Solo dígitos del número nacional (ej. 10 en México). */
export const sanitizeNationalPhoneDigits = (value, maxDigits = 10) => {
    const digits = sanitizeIntegerInput(value);
    return digits.slice(0, maxDigits);
};

/**
 * Separa un teléfono guardado (ej. +525551234567) en prefijo y parte nacional.
 * @param {string} stored
 * @param {Array<{value: string}>} prefixOptions
 * @param {string} defaultPrefix
 */
export const parseStoredPhone = (stored, prefixOptions, defaultPrefix = '+52') => {
    const raw = String(stored ?? '').trim();
    if (!raw) {
        return {prefix: defaultPrefix, national: ''};
    }

    const sorted = [...prefixOptions].sort((a, b) => b.value.length - a.value.length);
    for (const {value: prefix} of sorted) {
        if (raw.startsWith(prefix)) {
            const national = sanitizeNationalPhoneDigits(raw.slice(prefix.length), 15);
            return {prefix, national: national.slice(0, 10)};
        }
    }

    const digitsOnly = sanitizeIntegerInput(raw);
    if (digitsOnly.length === 10) {
        return {prefix: defaultPrefix, national: digitsOnly};
    }

    if (digitsOnly.length === 12 && digitsOnly.startsWith('52')) {
        return {prefix: '+52', national: digitsOnly.slice(2).slice(0, 10)};
    }

    if (digitsOnly.length === 13 && digitsOnly.startsWith('521')) {
        return {prefix: '+521', national: digitsOnly.slice(3).slice(0, 10)};
    }

    return {prefix: defaultPrefix, national: digitsOnly.slice(0, 10)};
};

export const combinePhoneForPayload = (prefix, nationalDigits) => {
    const p = String(prefix ?? '').trim();
    const n = sanitizeNationalPhoneDigits(nationalDigits, 10);
    if (!n) {
        return '';
    }
    return `${p}${n}`;
};

/** Fecha local YYYY-MM-DD (para atributo max de type="date" y comparaciones). */
export const getTodayIsoLocal = () => {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** Parsea fecha (YYYY-MM-DD) y hora (HH:mm o HH:mm:ss) en hora local. */
export const parseLocalDateTime = (dateIso, time) => {
    if (!dateIso || !time) {
        return null;
    }

    const normalizedTime = String(time).length >= 5 ? String(time).slice(0, 5) : String(time);
    const parsed = new Date(`${dateIso}T${normalizedTime}:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** true si dateIso (YYYY-MM-DD) es un día anterior a hoy en zona local. */
export const isDateBeforeToday = dateIso => Boolean(dateIso) && dateIso < getTodayIsoLocal();

/** true si la combinación fecha + hora ya ocurrió (zona local). */
export const isDateTimeInPast = (dateIso, time) => {
    const dt = parseLocalDateTime(dateIso, time);
    if (!dt) {
        return false;
    }

    return dt.getTime() < Date.now();
};

/** Hora local HH:mm para atributo min de input type="time" cuando la fecha es hoy. */
export const getNowTimeLocal = () => {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
};
